import React, { Component } from "react";

import Tooltip from "../tooltip";
import BlockImage from "./blockImage";

import MapModes from "./json/mapModes.json";
import SupportedVersions from "./json/supportedVersions.json";

import "./blockSelection.css";

class BlockSelection extends Component {
  cssRGB(RGBArray) {
    return `rgb(${RGBArray.join(", ")})`;
  }

  /**
   * Convert RGB [0-255] to HSL. Returns [h (0-360), s (0-1), l (0-1)].
   */
  rgbToHsl(rgb) {
    const r = rgb[0] / 255;
    const g = rgb[1] / 255;
    const b = rgb[2] / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l]; // achromatic
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
    return [h * 360, s, l];
  }

  /**
   * Sort colour set entries by hue, with grayscale (low saturation) first.
   */
  sortByHue(entries) {
    return [...entries].sort(([, a], [, b]) => {
      const hslA = this.rgbToHsl(a.tonesRGB.normal);
      const hslB = this.rgbToHsl(b.tonesRGB.normal);
      const isGrayA = hslA[1] < 0.1;
      const isGrayB = hslB[1] < 0.1;
      // Grayscale first
      if (isGrayA && !isGrayB) return -1;
      if (!isGrayA && isGrayB) return 1;
      // Within grayscale, sort by lightness
      if (isGrayA && isGrayB) return hslA[2] - hslB[2];
      // Otherwise sort by hue, then saturation
      if (Math.abs(hslA[0] - hslB[0]) > 1) return hslA[0] - hslB[0];
      return hslA[1] - hslB[1];
    });
  }

  getColourSetBox = (colourSet) => {
    const { optionValue_staircasing } = this.props;
    let background;
    switch (optionValue_staircasing) {
      case MapModes.SCHEMATIC_NBT.staircaseModes.OFF.uniqueId:
      case MapModes.MAPDAT.staircaseModes.OFF.uniqueId: {
        background = this.cssRGB(colourSet.tonesRGB.normal);
        break;
      }
      case MapModes.SCHEMATIC_NBT.staircaseModes.CLASSIC.uniqueId:
      case MapModes.SCHEMATIC_NBT.staircaseModes.VALLEY.uniqueId:
      case MapModes.MAPDAT.staircaseModes.ON.uniqueId: {
        background = `linear-gradient(${this.cssRGB(colourSet.tonesRGB.dark)} 33%, ${this.cssRGB(colourSet.tonesRGB.normal)} 33%, ${this.cssRGB(
          colourSet.tonesRGB.normal
        )} 66%, ${this.cssRGB(colourSet.tonesRGB.light)} 66%)`;
        break;
      }
      case MapModes.MAPDAT.staircaseModes.ON_UNOBTAINABLE.uniqueId: {
        background = `linear-gradient(${this.cssRGB(colourSet.tonesRGB.unobtainable)} 25%, ${this.cssRGB(colourSet.tonesRGB.dark)} 25%, ${this.cssRGB(
          colourSet.tonesRGB.dark
        )} 50%, ${this.cssRGB(colourSet.tonesRGB.normal)} 50%, ${this.cssRGB(colourSet.tonesRGB.normal)} 75%, ${this.cssRGB(colourSet.tonesRGB.light)} 75%)`;
        break;
      }
      case MapModes.SCHEMATIC_NBT.staircaseModes.FULL_DARK.uniqueId:
      case MapModes.MAPDAT.staircaseModes.FULL_DARK.uniqueId: {
        background = this.cssRGB(colourSet.tonesRGB.dark);
        break;
      }
      case MapModes.SCHEMATIC_NBT.staircaseModes.FULL_LIGHT.uniqueId:
      case MapModes.MAPDAT.staircaseModes.FULL_LIGHT.uniqueId: {
        background = this.cssRGB(colourSet.tonesRGB.light);
        break;
      }
      case MapModes.MAPDAT.staircaseModes.FULL_UNOBTAINABLE.uniqueId: {
        background = this.cssRGB(colourSet.tonesRGB.unobtainable);
        break;
      }
      default: {
        throw new Error("Unknown staircasing value");
      }
    }
    return (
      <div
        className="colourSetBox"
        style={{
          background: background,
        }}
      />
    );
  };

  render() {
    const {
      coloursJSON,
      getLocaleString,
      onChangeColourSetBlock,
      optionValue_version,
      selectedBlocks,
      presets,
      selectedPresetName,
      onPresetChange,
    } = this.props;

    return (
      <div className="section blockSelectionDiv">
        <h2 id="blockselectiontitle">{getLocaleString("BLOCK-SELECTION/TITLE")}</h2>
        <div className="presetRow">
          <span className="presetLabel">{getLocaleString("BLOCK-SELECTION/PRESETS/TITLE")}:</span>
          <select id="presets" value={selectedPresetName} onChange={onPresetChange}>
            <option value="None">{getLocaleString("BLOCK-SELECTION/PRESETS/NONE")}</option>
            {presets.map((preset) => (
              <option value={preset.name} key={preset.name}>
                {"localeKey" in preset ? getLocaleString(preset.localeKey) : preset.name}
              </option>
            ))}
          </select>
        </div>
        <div className="blockSelectionGrid">
          {this.sortByHue(
            Object.entries(coloursJSON)
              .filter(([, colourSet]) => Object.values(colourSet.blocks).some((block) => Object.keys(block.validVersions).includes(optionValue_version.MCVersion)))
          ).map(([colourSetId, colourSet]) => (
              <div key={colourSetId} className="colourSet">
                {this.getColourSetBox(colourSet)}
                <label>
                  <Tooltip tooltipText={getLocaleString("NONE")}>
                    <BlockImage
                      getLocaleString={getLocaleString}
                      coloursJSON={coloursJSON}
                      colourSetId={colourSetId}
                      blockId={"-1"}
                      onClick={() => onChangeColourSetBlock(colourSetId, "-1")}
                      style={{
                        cursor: "pointer",
                        ...(selectedBlocks[colourSetId] === "-1" && {
                          filter: "drop-shadow(0 0 4px #658968)",
                          backgroundColor: "#658968",
                        }),
                      }}
                    />
                  </Tooltip>
                </label>
                <div className={"colourSetBlocks"}>
                  {Object.entries(colourSet.blocks)
                    .filter(([, block]) => Object.keys(block.validVersions).includes(optionValue_version.MCVersion))
                    .map(([blockId, block]) => (
                      <label key={blockId}>
                        <Tooltip tooltipText={block.displayName}>
                          {selectedBlocks[colourSetId] === blockId &&
                            !(Object.values(SupportedVersions)[Object.keys(SupportedVersions).length - 1].MCVersion in block.validVersions) && (
                              <div
                                style={{
                                  position: "absolute",
                                  paddingLeft: "28px",
                                  zIndex: 110,
                                }}
                              >
                                <Tooltip
                                  tooltipText={`${getLocaleString("BLOCK-SELECTION/UNSUPPORTED-PAST")} ${
                                    Object.keys(block.validVersions)[Object.keys(block.validVersions).length - 1]
                                  }`}
                                  textStyleOverrides={{
                                    whiteSpace: "nowrap",
                                    backgroundColor: "red",
                                  }}
                                >
                                  <h2 style={{ backgroundColor: "red", margin: "0" }}>{"!"}</h2>
                                </Tooltip>
                              </div>
                            )}
                          <BlockImage
                            coloursJSON={coloursJSON}
                            colourSetId={colourSetId}
                            blockId={blockId}
                            onClick={() => onChangeColourSetBlock(colourSetId, blockId)}
                            style={{
                              cursor: "pointer",
                              ...(selectedBlocks[colourSetId] === blockId && {
                                filter: "drop-shadow(0 0 4px #658968)",
                                ...(block.presetIndex !== "CUSTOM" && {
                                  backgroundColor: "#658968",
                                }),
                              }),
                            }}
                          />
                        </Tooltip>
                      </label>
                    ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  }
}

export default BlockSelection;
